import { useCallback, useEffect, useState } from "react";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import {
  deleteAllSavedSearch,
  deleteSavedDocument,
  deleteSavedSearch,
  getSavedDocument,
  getSavedSearchById,
  getSavedSearches,
  SavedSearch,
} from "../../../services";
import {
  getCurrentModelTypeFilters,
  getLoggedUser,
} from "../../../store/selectors";
import { Pagewrapper } from "../../components";
import { Tabs } from "../../components/_tab";
import PDLTypography from "../../components/dna/_pdltypography";

import {
  doSearch,
  SEARCH_TYPES,
  setAppliedFilters,
  setCurrentInputQuery,
  setModelTypeAppliedFilter,
  setQueryValue as setQueryValueRedux,
  setSearchModelType,
  setSearchPeriod,
  setSearchType,
  setSelectedPersonFilter,
} from "../../../store/reducer/_searchReducer";
import { notify } from "../../../utils/_notify";

import { ProfileProps } from "..";
import { SortOrder } from "../../components/_saveDateFilter";
import LexLoading from "../../components/global/_lexLoading";
import SavedItemsList from "./_savedItemsList";
import SavedSearchesList from "./_savedSearchesList";
import "./index.scss";

type ProfileActiveTab = "saved" | "savedSearches";

const Profile = (props: ProfileProps) => {
  const [activeTab, setActiveTab] = useState<ProfileActiveTab>("saved");
  const [order, setOrder] = useState<SortOrder>(`desc`);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [savedDocsPage, setSavedDocsPage] = useState<number>(1);
  const [savedDocsTotalPages, setSavedDocsTotalPages] = useState<number>(1);
  const [savedItems, setSavedItems] = useState<any[]>([]);
  const [savedSearches, setSavedSearches] = useState<SavedSearch>();
  const [loading, setLoading] = useState(false);
  const loggedUser = useSelector(getLoggedUser);
  const currentModelTypeFilters = useSelector(getCurrentModelTypeFilters);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userName = loggedUser?.name || "Usuário";

  const fetchSavedItems = useCallback(
    async (pageNumber: number) => {
      setLoading(true);
      try {
        const savedDocument = await getSavedDocument({
          type: "",
          page: pageNumber,
          limit,
        });
        const items = (savedDocument.itens || []).map((entry: any) => {
          if (entry?.documento) {
            return {
              ...entry.documento,
              type: entry.tipo,
              savedAt: entry.salvoEm,
              savedId: entry._id,
            };
          }

          return {
            _id: entry?._id,
            type: entry?.tipo,
            savedAt: entry?.salvoEm,
          };
        });

        setSavedItems(items);
        setSavedDocsPage(savedDocument.page || pageNumber);
        const totalPages = Math.max(
          1,
          Math.ceil((savedDocument.total || 0) / limit),
        );
        setSavedDocsTotalPages(totalPages);
      } catch (error) {
        console.error("Error fetching saved items:", error);
      } finally {
        setLoading(false);
      }
    },
    [limit],
  );

  const fetchSavedSearches = useCallback(
    async (pageNumber: number = page) => {
      if (activeTab === "savedSearches") {
        setLoading(true);
        try {
          const searches = await getSavedSearches(
            pageNumber,
            limit,
            "data",
            order || undefined,
          );
          setSavedSearches(searches);
        } catch (error) {
          console.error("Error fetching saved searches:", error);
        } finally {
          setLoading(false);
        }
      }
    },
    [activeTab, limit, order, page],
  );

  const handleDeleteSavedSearch = async (id: string) => {
    try {
      await deleteSavedSearch(id);
      await fetchSavedSearches();
      notify(dispatch, {
        message: "Pesquisa salva removida com sucesso!",
        title: "Sucesso",
        variant: "success",
      });
    } catch (error) {
      notify(dispatch, {
        message: "Erro ao remover pesquisa salva.",
        title: "Erro",
        variant: "danger",
      });
    }
  };

  const handleSortChange = async (newOrder: SortOrder) => {
    setOrder(newOrder);
    setPage(1);
    await fetchSavedSearches(1);
  };

  const handlePageChange = async (newPage: number) => {
    setPage(newPage);
    await fetchSavedSearches(newPage);
  };

  const handleRemoveSavedItem = async (item: any) => {
    try {
      const savedId = item?.savedId || item?._id;
      if (!savedId) return;
      await deleteSavedDocument(savedId);

      const shouldGoBack = savedDocsPage > 1 && savedItems.length <= 1;
      const nextPage = shouldGoBack ? savedDocsPage - 1 : savedDocsPage;

      if (shouldGoBack) {
        setSavedDocsPage(nextPage);
      }

      fetchSavedItems(nextPage);

      notify(dispatch, {
        message: "Documento removido dos salvos!",
        title: "Sucesso",
        variant: "success",
      });
    } catch (error) {
      notify(dispatch, {
        message: "Erro ao remover documento salvo.",
        title: "Erro",
        variant: "danger",
      });
    }
  };

  const handleRunSavedSearch = async (id: string) => {
    if (!id) return;
    try {
      setLoading(true);
      const detail = await getSavedSearchById(id);
      const url = detail.url || "";
      const queryString = url.includes("?") ? url.split("?")[1] : "";
      const searchParams = new URLSearchParams(queryString);
      const requestParams = {} as Record<string, string>;
      searchParams.forEach((value, key) => {
        requestParams[key] = value;
      });

      const params = Object.keys(requestParams).length
        ? requestParams
        : detail.params || {};

      const query = (params.q || detail.nome || "").toString();
      const collection = (params.collectionType || detail.tipo || "all")
        .toString()
        .toLowerCase();
      const matchedKey =
        (Object.keys(SEARCH_TYPES).find(
          (k) => k.toLowerCase() === collection,
        ) as keyof typeof SEARCH_TYPES) || "all";

      const searchTypeObj = SEARCH_TYPES[matchedKey] || SEARCH_TYPES.all;
      const personFilterKey = searchTypeObj?.personFilter?.param;

      const excludedKeys = new Set([
        "q",
        "limit",
        "page",
        "sort",
        "collectionType",
        "dataInicio",
        "dataFim",
        "tipoModelo",
      ]);

      if (personFilterKey) {
        excludedKeys.add(personFilterKey);
      }

      const appliedFiltersList = Object.entries(params)
        .filter(([key]) => !excludedKeys.has(key))
        .map(([key, value]) => ({
          id: key,
          values: String(value)
            .split(",")
            .map((entry) => entry.trim())
            .filter(Boolean),
        }))
        .filter(({ values }) => values.length > 0);

      const runPage = Number(params.page) || 1;

      dispatch(setSearchType(matchedKey));
      dispatch(setAppliedFilters({ appliedFilters: appliedFiltersList }));
      dispatch(
        setSearchPeriod({
          searchType: matchedKey,
          period: params.dataInicio || params.dataFim ? "custom" : "always",
          startDate: params.dataInicio || null,
          endDate: params.dataFim || null,
        }),
      );
      dispatch(
        setSelectedPersonFilter({
          searchType: matchedKey,
          value: personFilterKey ? params[personFilterKey] || "" : "",
        }),
      );

      if (matchedKey === "modelos") {
        const modelTypeValue = params.tipoModelo || "all";
        dispatch(setSearchModelType(modelTypeValue));

        const modelFilterIds = (currentModelTypeFilters || []).map(
          (filter: { id?: string }) => filter.id,
        );
        const modelFilterValues = Object.entries(params)
          .filter(([key]) => modelFilterIds.includes(key))
          .flatMap(([, value]) =>
            String(value)
              .split(",")
              .map((entry) => entry.trim())
              .filter(Boolean),
          );

        dispatch(
          setModelTypeAppliedFilter({
            modelType: modelTypeValue,
            filters: modelFilterValues,
          }),
        );
      }

      dispatch(setQueryValueRedux(query));
      await (dispatch as any)(
        doSearch({
          query,
          searchType: searchTypeObj,
          page: runPage,
          requestParams: params,
        } as any),
      );

      dispatch(setCurrentInputQuery(query));
      navigate("/resultados");
    } catch (err) {
      console.error("Error running saved search:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "saved") {
      fetchSavedItems(savedDocsPage);
      return;
    }
    if (activeTab === "savedSearches") {
      fetchSavedSearches();
    }
  }, [
    activeTab,
    page,
    savedDocsPage,
    order,
    fetchSavedItems,
    fetchSavedSearches,
  ]);

  return (
    <Pagewrapper {...props}>
      <Container>
        <Row>
          <Col className="mt-5">
            <PDLTypography size="xs" weight="semibold">
              Olá, {userName}!
            </PDLTypography>

            <PDLTypography size="md">
              Veja os seus itens salvos e o histórico de pesquisa.
            </PDLTypography>

            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as ProfileActiveTab)}
              tabs={[
                { key: "saved", label: "Salvos" },
                { key: "savedSearches", label: "Pesquisas salvas" },
              ]}
            />
          </Col>
        </Row>

        <Row className="mt-4">
          <Col>
            {loading ? (
              <div className="loading-content">
                <LexLoading customText="Buscando resultados..." />
              </div>
            ) : activeTab === "saved" ? (
              <SavedItemsList
                items={savedItems}
                currentPage={savedDocsPage}
                totalPages={savedDocsTotalPages}
                onPageChange={(newPage) => setSavedDocsPage(newPage)}
                onRemove={handleRemoveSavedItem}
              />
            ) : (
              <SavedSearchesList
                searches={savedSearches?.items ?? []}
                onDeleteAll={async () => {
                  try {
                    await deleteAllSavedSearch();
                    await fetchSavedSearches();
                    notify(dispatch, {
                      message: "Pesquisas salvas removidas com sucesso!",
                      title: "Sucesso",
                      variant: "success",
                    });
                  } catch (error) {
                    notify(dispatch, {
                      message: "Erro ao remover pesquisas salvas.",
                      title: "Erro",
                      variant: "danger",
                    });
                  }
                }}
                onDelete={(id) => {
                  handleDeleteSavedSearch(id);
                }}
                onSortChange={handleSortChange}
                onRun={handleRunSavedSearch}
                currentOrder={order}
                currentPage={page}
                totalPages={savedSearches?.totalPages || 1}
                onPageChange={handlePageChange}
              />
            )}
          </Col>
        </Row>
      </Container>
    </Pagewrapper>
  );
};

export default Profile;
